/** An undo stack that manages tracking and reverting actions by storing objects that represent changes. */
export class DeltaUndoStack<Delta> {
  /** Stack of undo deltas. */
  private stack: Delta[] = [];

  /** Current undo index in stack.
   * `stack[index]` gives the delta of the last undone action.
   * If `index == stack.length`, nothing was undone. */
  private index: number = 0;

  constructor(
    /** A function called when an action needs to be undone. The undo `delta` is passed. */
    public undoer: (delta: Delta) => void,
    /** A function called when an action needs to be redone. The `delta` to redo is passed. */
    public redoer: (delta: Delta) => void,
    /** The max number of undos allowed. Defaults to 20. Set to negative for no limit, or to 0 to effectively disable undo/redo. */
    public maxUndos: number = 20
  ) {}

  private truncateMaxUndos(): boolean {
    // if we ran out of undo stack size, cut off oldest undos
    if (this.maxUndos >= 0 && this.stack.length > this.maxUndos) {
      const cutOff = this.stack.length - this.maxUndos;
      this.stack.splice(0, cutOff);

      this.index -= cutOff;
      return true;
    }
    return false;
  }

  /** Modify max undos. (Set to 0 to clear stack and disable undo/redo.) */
  setMaxUndos(maxUndos: number) {
    this.maxUndos = maxUndos;

    // truncate undos at new max
    if (this.truncateMaxUndos()) {
      this.onStackChanged?.(this);
    }
  }

  /** Push a state change to the stack. */
  push(delta: Delta) {
    // truncate the redo stack
    this.stack.length = this.index;

    // push delta
    this.index = this.stack.push(delta);

    // truncate undos at maxUndos
    this.truncateMaxUndos();

    this.onStackChanged?.(this);
  }

  /** Undo an action. */
  undo() {
    // if undo is available...
    if (this.hasUndo()) {
      // step back and undo-apply delta
      this.undoer(this.stack[--this.index]);

      this.onStackChanged?.(this);
    }
  }

  /** Redo an action. */
  redo() {
    // if redo is available...
    if (this.hasRedo()) {
      // redo-apply delta and step forward
      this.redoer(this.stack[this.index++]);

      this.onStackChanged?.(this);
    }
  }

  /** Get the number of undos available. If truthy, undo is allowed. */
  hasUndo(): number {
    // undo is available if index is not at start
    return this.index;
  }

  /** Get the number of redos available. If truthy, redo is allowed. */
  hasRedo(): number {
    // redo is available as long as index is not past last index
    return this.stack.length - this.index;
  }

  /** Get the next undo delta object, or null if none. */
  nextUndo(): Delta | null {
    return this.stack[this.index - 1] ?? null;
  }

  /** Get the next redo delta object, or null if none. */
  nextRedo(): Delta | null {
    return this.stack[this.index] ?? null;
  }

  /** A handler to be called whenever the stack or current undo index changes. */
  onStackChanged?: (undoStack: DeltaUndoStack<Delta>) => void;
}
